import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Divider, Stack, Typography } from "@mui/material";
import { useLinkToCustomerMutation } from "Auth/Api/Mutations/Migration/useLinkToCustomerMutation";
import { useLinkToCustomerSsoMutation } from "Auth/Api/Mutations/Migration/useLinkToCustomerSsoMutation";
import { AppleButton } from "Components/Auth/Apple/AppleButton";
import { GoogleButton } from "Components/Auth/Google/GoogleButton";
import { useMigrationSessionHandoff } from "Components/Migration/useMigrationSessionHandoff";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { FormPasswordInput } from "Components/Shared/Inputs/Form/FormPasswordInput";
import { type AppleFlow, IdentityProvider } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { isAndroid } from "Utils/Platform/PlatformUtils";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const COMPONENT_RESOURCES = Resources.Auth.Migration;

const schema = z.object({ login: z.email(), password: z.string().min(1) });
type FormData = z.infer<typeof schema>;

type Props = {
  onBack: () => void;
  /**
   * Authorization code the Google round trip brought back in the URL. Consumed once on mount:
   * the code is single-use and the trip already reloaded the page.
   */
  googleCode?: string | null;
  /** The Google round trip came back without the nonce this tab issued, so its code was dropped. */
  hasRejectedGoogleReturn?: boolean;
};

export const LinkToCustomerForm = ({
  onBack,
  googleCode,
  hasRejectedGoogleReturn = false,
}: Props) => {
  const { t } = useResource();
  const [serverError, setServerError] = useState<string | null>(
    hasRejectedGoogleReturn ? t(COMPONENT_RESOURCES.LinkSsoError) : null,
  );
  const handoffSession = useMigrationSessionHandoff();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { login: "", password: "" },
  });

  const { mutate: linkToCustomer, isPending: isPasswordLinkPending } =
    useLinkToCustomerMutation({
      onSuccess: handoffSession,
      onError: () => {
        setServerError(t(COMPONENT_RESOURCES.LinkError));
      },
    });

  const { mutate: linkToCustomerSso, isPending: isSsoLinkPending } =
    useLinkToCustomerSsoMutation({
      onSuccess: handoffSession,
      onError: (error) => {
        // 404 = no Identity uses the provider's e-mail as its login (a different account, or
        // Apple's private relay address) — the one failure the user can act on themselves.
        const status = (error as { status?: number } | undefined)?.status;
        setServerError(
          t(
            status === 404
              ? COMPONENT_RESOURCES.LinkSsoNoAccount
              : COMPONENT_RESOURCES.LinkSsoError,
          ),
        );
      },
    });

  const isPending = isPasswordLinkPending || isSsoLinkPending;

  // StrictMode mounts twice in dev and the code is single-use, so guard the auto-submit.
  const isGoogleCodeConsumed = useRef(false);
  useEffect(() => {
    if (!googleCode || isGoogleCodeConsumed.current) {
      return;
    }
    isGoogleCodeConsumed.current = true;
    linkToCustomerSso({
      identityProvider: IdentityProvider.Google,
      token: googleCode,
    });
  }, [googleCode, linkToCustomerSso]);

  const onSubmit = ({ login, password }: FormData) => {
    setServerError(null);
    linkToCustomer({ login, password });
  };

  const handleAppleAuthorized = (code: string, appleFlow: AppleFlow) => {
    setServerError(null);
    linkToCustomerSso({
      identityProvider: IdentityProvider.Apple,
      token: code,
      appleFlow,
    });
  };

  return (
    <Stack gap={3}>
      <Typography variant="h6">
        {t(COMPONENT_RESOURCES.LinkToCustomerTitle)}
      </Typography>
      <Typography variant="body2">
        {t(COMPONENT_RESOURCES.LinkToCustomerDescription)}
      </Typography>

      {/* The SSO buttons sit outside the form so a third-party button can never submit it. */}
      <Stack gap={3} component="form" onSubmit={handleSubmit(onSubmit)}>
        <FormInput
          name="login"
          control={control}
          errors={errors}
          label={t(COMPONENT_RESOURCES.LoginLabel)}
        />

        <FormPasswordInput
          name="password"
          control={control}
          label={t(COMPONENT_RESOURCES.PasswordLabel)}
        />

        <PrimaryButton
          type="submit"
          variant="contained"
          color="primary"
          disabled={isPending}
        >
          {t(COMPONENT_RESOURCES.LinkButton)}
        </PrimaryButton>
      </Stack>

      <Divider>{t(COMPONENT_RESOURCES.LinkSsoDivider)}</Divider>

      <Typography variant="body2">
        {t(COMPONENT_RESOURCES.LinkSsoDescription)}
      </Typography>

      <Stack gap={2}>
        <GoogleButton isLoading={isPending} mode="link" />
        {/* The Android shell runs Apple sign-in as a browser redirect that lands on
            /auth/apple and signs in — it cannot hand the code back to this form. Link
            mode via Apple is deferred there; the person can finish the link in a browser. */}
        {isAndroid() ? (
          <Typography variant="body2" color="text.secondary">
            {t(Resources.Auth.SSO.Apple.NotSupportedInApp)}
          </Typography>
        ) : (
          <AppleButton
            isLoading={isPending}
            onAuthorized={handleAppleAuthorized}
          />
        )}
      </Stack>

      {serverError && <Alert severity="error">{serverError}</Alert>}

      <PrimaryButton
        variant="outlined"
        color="secondary"
        onClick={onBack}
        disabled={isPending}
      >
        {t(COMPONENT_RESOURCES.BackButton)}
      </PrimaryButton>
    </Stack>
  );
};
